import { useEffect, useState } from "react";
import { Loader2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-white";

export default function ContactPage() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/company").then((r) => setC(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message received. We reply within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20" data-testid="contact-page">
      <span className="eyebrow">Contact</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl">Talk To A <span className="text-gold">Producer.</span></h1>
      <div className="mt-12 grid gap-10 md:grid-cols-5">
        <div className="space-y-5 md:col-span-2">
          {c?.phone && <div className="flex gap-3 text-neutral-300"><Phone className="h-5 w-5 text-neutral-500" /><a href={`tel:${c.phone}`} data-testid="contact-phone">{c.phone}</a></div>}
          {c?.email && <div className="flex gap-3 text-neutral-300"><Mail className="h-5 w-5 text-neutral-500" /><a href={`mailto:${c.email}`} data-testid="contact-email">{c.email}</a></div>}
          <div className="flex gap-3 text-neutral-300"><MapPin className="h-5 w-5 text-neutral-500" /><span data-testid="contact-address">{[c?.address, c && `${c.city}, ${c.state} ${c.zip}`].filter(Boolean).join(", ")}</span></div>
          <p className="text-sm text-neutral-500">{c?.hours}</p>
          <p className="pt-4 text-sm text-neutral-400">For event quotes use the <a href="/request" className="text-white underline">request wizard</a> — it captures everything a producer needs to price your event in one business day.</p>
        </div>
        {sent ? (
          <div className="glass rounded-3xl p-8 md:col-span-3" data-testid="contact-sent">
            <h2 className="font-display text-3xl uppercase text-white">Received.</h2>
            <p className="mt-3 text-neutral-300">Thanks {form.name}. A producer will reply to {form.email} within one business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="glass grid gap-3 rounded-3xl p-6 sm:grid-cols-2 md:col-span-3 md:p-8" data-testid="contact-form">
            <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} />
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} />
            <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className={inputCls} />
            <input placeholder="Subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" className={inputCls} />
            <textarea required rows={5} placeholder="How can we help?" value={form.message} onChange={set("message")} data-testid="contact-message" className={`${inputCls} sm:col-span-2`} />
            <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-gold rounded-full py-3.5 font-display text-lg uppercase disabled:opacity-60 sm:col-span-2">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
